import {Alert, Button, Collapse, CollapseProps, message, Modal, Radio, Select} from "antd";
import {
    DeliveryMethod,
    DiscountCodeApi,
    DiscountCodeDto, DiscountCodeResponseDto, DiscountCodeValidationMessage, OrderApi,
    OrderDeliveryVariantCostDto,
    OrderDetailsDto, OrderStage
} from "../../../api";
import deliveryMethodTranslation from "../../../helpers/deliveryMethodHelper";
import InpostMap from "../../../components/inpost/inpostMap/inpostMap";
import React, {useCallback, useEffect, useState} from "react";
import {useInjection} from "inversify-react";
import ApiService from "../../../services/apiService";
import {DeliveryMethodEnum, PaymentMethod, PaymentState} from "./orderDetailsContainer";
import {AsyncButton} from "digimuth-components/dist";
import styles from "./orderDataFill.module.scss";
import discountCodeValidationMessageTranslation, {discountTypeTranslation} from "../../../helpers/DiscountTypeHelper";
import {InpostPointType} from "../../../components/inpost/inpost-geowidget/inpost-geowidget";

interface OrderDataFillProps {
    orderDetails: OrderDetailsDto | null,
    showOrderDataModal: boolean,
    setShowOrderDataModal: (show: boolean) => void,
    paymentMethods: PaymentMethod[],
    selectedPaymentMethod: PaymentMethod | null,
    setSelectedPaymentMethod: (method: PaymentMethod) => void,
    selectedDeliveryMethod: DeliveryMethod | null,
    setSelectedDeliveryMethod: (method: DeliveryMethod) => void,
    availableDeliveryMethods: OrderDeliveryVariantCostDto[] | null,
    selectedParcelMachine: InpostPointType | null,
    setSelectedParcelMachine: (parcelMachine: InpostPointType | null) => void,
    refreshOrderDetails: () => Promise<any>,
    updateStock: () => Promise<any>;
}

export default function OrderDataFill(props: OrderDataFillProps) {
    const apiService = useInjection(ApiService);

    const [codes, setCodes] = useState<DiscountCodeDto[]>();
    const [activeKey, setActiveKey] = useState<string>("discountCodeTab");
    const [discountCodeId, setDiscountCodeId] = useState<number | null>(null);
    const [discountCodeResponse, setDiscountCodeResponse] = useState<DiscountCodeResponseDto | null>(null);

    const fetchDiscountCodes = useCallback(async () => {
        try {
            const response = await apiService.getApi(DiscountCodeApi)
                .apiDiscountCodeGet();
            setCodes(response.data);
        } catch (e: any) {
            return e.response?.data;
        }
    }, [apiService]);

    const isPaymentMethodDisabled = (method: PaymentMethod): boolean => {
        const {state, minValue, maxValue} = method;
        if (state === PaymentState.Disabled || state === PaymentState.TemporaryDisabled) return true;
        if ((props.orderDetails?.orderValue ?? 0) < minValue) return true;
        return !!(maxValue && (props.orderDetails?.orderValue ?? 0) > maxValue);
    };

    const onDiscountCodeApply = useCallback(async () => {
        if (!!props.orderDetails?.discountCodeId || !discountCodeId) {
            setActiveKey("deliveryMethodTab");
            return;
        }
        try {
            // const response = await apiService.getApi(OrderApi)
            //     .apiOrderApplyCodePatch(props.orderDetails?.orderGuid, discountCodeId);
            // setDiscountCodeResponse(response.data);
            setTimeout(() => setActiveKey("deliveryMethodTab"), 1500);
        } catch (e: any) {
            return e.response?.data;
        }
    }, [apiService, discountCodeId, props.orderDetails?.orderGuid, props.orderDetails?.discountCodeId]);

    const onDeliveryMethodSelect = useCallback(async () => {
        try {
            await apiService.getApi(OrderApi)
                .apiOrderOrderGuidFillOrderDeliveryDetailsPut(props.orderDetails?.orderGuid!, {
                    deliveryMethod: props.selectedDeliveryMethod!,
                    inPostParcelMachineDetails: {
                        parcelLockerId: props.selectedParcelMachine?.name,
                        addressLine1: props.selectedParcelMachine?.address.line1,
                        addressLine2: props.selectedParcelMachine?.address.line2,
                        locationDescription: props.selectedParcelMachine?.location_description
                    }
                });
            setActiveKey("paymentMethodTab");
        } catch (e: any) {
            return e.response?.data;
        }
    }, [apiService, props.selectedParcelMachine, props.selectedDeliveryMethod, props.orderDetails?.orderGuid]);

    const onPaymentMethodSelect = useCallback(async () => {
        try {
            await apiService.getApi(OrderApi)
                .apiOrderOrderGuidFillOrderPaymentDetailsPut(props.orderDetails?.orderGuid!, {
                    paymentMethod: {name: props.selectedPaymentMethod?.label, value: props.selectedPaymentMethod?.value}
                });
            await props.updateStock();
            await apiService.getApi(OrderApi)
                .apiOrderStatePatch(props.orderDetails?.orderGuid!, OrderStage.WaitingForPayment);
            props.setShowOrderDataModal(false);
            await props.refreshOrderDetails();
            if (props.selectedPaymentMethod?.value !== "ShopPayment") {
                message.info("Link z płatnością za zamówienie został wysłany na podany adres email");
            }
        } catch (e: any) {
            return e.response?.data;
        }
    }, [apiService, props.selectedPaymentMethod, props.orderDetails]);

    const getDiscountCodeMessageType = () => {
        switch (discountCodeResponse?.responseMessage) {
            case DiscountCodeValidationMessage.Ok:
                return "success";
            case DiscountCodeValidationMessage.CodeIncorrect:
                return "error";
            case DiscountCodeValidationMessage.OrderAmountNotReached:
            case DiscountCodeValidationMessage.CodeExpired:
                return "info";
        }
    }

    const onCodeRemoveClick = async () => {
        setDiscountCodeId(null);
        if (!props.orderDetails?.discountCodeId) return;
        // await apiService.getApi(OrderApi).apiOrderApplyCodePatch(props.orderDetails?.orderGuid!, undefined);
        // await props.refreshOrderDetails();
    }

    useEffect(() => setDiscountCodeId(props.orderDetails?.discountCodeId ?? null), [props.orderDetails]);

    useEffect(() => {
        fetchDiscountCodes();
    }, [fetchDiscountCodes]);

    const items: CollapseProps['items'] = [
        {
            key: "discountCodeTab",
            label: "Kod rabatowy",
            children: <div className={styles.discountCodeContainer}>
                <div className={styles.headerText}>Wybierz kod rabatowy dla zamówienia</div>
                <div className={styles.discountCodeSelectContainer}>
                    <Select
                        value={props.orderDetails?.discountCode?.id || discountCodeId}
                        placeholder={"Wybierz kod rabatowy dla zamówienia..."}
                        style={{width: "100%"}} showSearch onChange={setDiscountCodeId}
                        disabled={!!props.orderDetails?.discountCodeId}
                    >
                        {codes?.map((code, index) => <Select.Option
                            key={index} value={code.id} label={code.code}>
                            <div>
                                <strong>{code.code}</strong> {` - ${discountTypeTranslation(code.discountType!)?.translation} 
                    ${code.discountValue ? `(${code.discountValue} ${discountTypeTranslation(code.discountType!)?.sign})` : ""}`}
                            </div>
                        </Select.Option>)}
                    </Select>
                    <div className={styles.buttonsContainer}>
                        <AsyncButton
                            type={"primary"}
                            onClick={onDiscountCodeApply}
                            className={styles.discountCodeButton}
                        >
                            {!discountCodeId || props.orderDetails?.discountCodeId ? "Dalej" : "Zapisz"}
                        </AsyncButton>
                        <AsyncButton
                            disabled={!discountCodeId && !props.orderDetails?.discountCodeId}
                            onClick={onCodeRemoveClick}
                        >
                            Usuń
                        </AsyncButton>
                    </div>
                </div>
                {discountCodeResponse && <Alert
                    message={discountCodeValidationMessageTranslation(discountCodeResponse.responseMessage!)}
                    type={getDiscountCodeMessageType()}
                />}
            </div>
        }, {
            key: "deliveryMethodTab",
            label: "Metoda dostawy",
            children: <div className={styles.deliveryMethodContainer}>
                <div className={styles.headerText}>Wybierz metodę dostawy zamówienia</div>
                <Radio.Group
                    className={styles.methodsContainer}
                    value={props.selectedDeliveryMethod}
                    onChange={e => props.setSelectedDeliveryMethod(e.target.value)}
                >
                    {props.availableDeliveryMethods?.map((method: OrderDeliveryVariantCostDto, index: number) =>
                        <Radio
                            key={index}
                            value={method.deliveryMethod}
                        >
                            <img
                                alt={method.deliveryMethod!} className={styles.icon}
                                src={DeliveryMethodEnum.find(x => x.value === method.deliveryMethod)?.iconSrc}
                            />
                            <div className={styles.text}>
                                {deliveryMethodTranslation(method.deliveryMethod!)}
                            </div>
                            <div className={styles.cost}>
                                <strong>{method.deliveryCost} zł</strong>
                            </div>
                        </Radio>
                    )}
                </Radio.Group>
                {props.selectedDeliveryMethod === DeliveryMethod.InPostParcelMachine &&
                    <div className={styles.inPostMapContainer}>
                        {props.selectedParcelMachine && <div className={styles.parcelMachineDetailsContainer}>
                            <div className={styles.text}>
                                Wybrano paczkomat: <strong>{props.selectedParcelMachine.name}</strong>
                                <div>
                                    <span>{props.selectedParcelMachine.address.line1}</span>
                                    <br/>
                                    <span>{props.selectedParcelMachine.address.line2}</span>
                                    <br/>
                                    <br/>
                                    <span>{props.selectedParcelMachine.location_description}</span>
                                </div>
                            </div>
                            <Button
                                className={styles.changeButton}
                                type={"primary"}
                                onClick={() => props.setSelectedParcelMachine(null)}
                            >
                                Zmień paczkomat
                            </Button>
                        </div>}
                        <InpostMap
                            style={{
                                height: !props.selectedParcelMachine ? "70vh" : 0,
                                margin: !props.selectedParcelMachine ? "20px 0" : 0
                            }}
                            onChange={props.setSelectedParcelMachine}
                            className={styles.inPostMap}
                        />
                    </div>}
                <div className={styles.buttonContainer} style={{marginTop: "20px"}}>
                    <AsyncButton
                        type={"primary"}
                        onClick={onDeliveryMethodSelect}
                        className={styles.button}
                        disabled={props.selectedDeliveryMethod === DeliveryMethod.InPostParcelMachine && !props.selectedParcelMachine}
                    >
                        Zapisz
                    </AsyncButton>
                </div>
            </div>
        }, {
            key: "paymentMethodTab",
            label: "Metoda płatności",
            children: <>
                <Radio.Group
                    onChange={e => props.setSelectedPaymentMethod(e.target.value)}
                    value={props.selectedPaymentMethod}
                    className={styles.paymentMethodsContainer}
                >
                    {props.paymentMethods.map((method, index) => <Radio
                            key={index} value={method}
                            disabled={isPaymentMethodDisabled(method)}
                            className={styles.paymentMethod}
                        >
                            <div className={styles.methodDetails}>
                                {method.icon && <img
                                    className={styles.methodIcon}
                                    alt={method.value}
                                    src={method.icon}
                                />}
                                <div className={styles.methodLabel}>
                                    {method.label}
                                </div>
                            </div>
                        </Radio>
                    )}
                </Radio.Group>
                <div className={styles.buttonContainer}>
                    <AsyncButton type={"primary"} onClick={onPaymentMethodSelect} className={styles.button}>
                        Zapisz
                    </AsyncButton>
                </div>
            </>
        }
    ]

    return <Modal
        width={850}
        open={props.showOrderDataModal}
        title={"Wypełnij dane o zamówieniu"}
        onCancel={() => {
            props.setShowOrderDataModal(false);
            setActiveKey("discountCodeTab")
        }}
        footer={null}
    >
        <Collapse
            items={items}
            defaultActiveKey={"discountCodeTab"}
            activeKey={activeKey}
        />
    </Modal>
}